import React, { useEffect, useRef, useState } from 'react';
import FloatingLabel from '../FloatingLabel';
import FFLink from '../FFLink';
import LoadingSpinner from '../icons/LoadingSpinner';
import RightArrowIcon from '../icons/RightArrowIcon';
import CheckIcon from '../icons/CheckIcon';
import useSiteMetadata from '../../utils/useSiteMetaData';
import styles from './EmailForm.module.css';

const ERROR_MESSAGES = {
  INVALID_EMAIL: 'Please enter a valid email address',
  UNKNOWN_ERROR: 'Something went wrong, please try again',
};

/**
 * @param {object} props
 * @param {string=} props.className
 */
export default function EmailFooterForm({ className = '' }) {
  const [emailError, setEmailError] = useState();
  const [emailAddress, setEmailAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormCompleted, setFormCompleted] = useState(false);
  const { awsApiGatewayUrl } = useSiteMetadata();

  useEffect(() => {
    /** @type {HTMLInputElement|null} emailInputEl */
    const emailInputEl = document.querySelector('input#webFooter-emailAddress');
    if (emailInputEl?.value) {
      setEmailAddress(emailInputEl.value);
    }
  }, []);

  /** @type {React.MutableRefObject<HTMLParagraphElement|null>} successMessageRef */
  const successMessageRef = useRef(null);
  useEffect(() => {
    if (isFormCompleted && successMessageRef.current) {
      successMessageRef.current.focus();
    }
  }, [isFormCompleted, successMessageRef.current]);

  /** @param {React.FormEvent} event */
  async function handleSubmit(event) {
    event.preventDefault();
    setEmailError(undefined);
    setIsSubmitting(true);

    try {
      await fetch(`${awsApiGatewayUrl}/subscribeEmail`, {
        body: JSON.stringify({
          email: emailAddress,
          emailPromotions: true,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      }).then(async res => {
        if (!res.ok) {
          const responseBody = await res.json();
          const { error } = responseBody;
          throw new Error(
            error === 'Invalid Input' ? 'INVALID_EMAIL' : 'UNKNOWN_ERROR',
          );
        }
        return res.json();
      });

      setFormCompleted(true);
    } catch (/** @type {any} error */ error) {
      setEmailError(
        // @ts-ignore
        ERROR_MESSAGES[error.message] ? error.message : 'UNKNOWN_ERROR',
      );
    }
    setIsSubmitting(false);
  }

  let Button;
  if (isFormCompleted) {
    Button = <CheckIcon />;
  } else if (isSubmitting) {
    Button = <LoadingSpinner className={styles.spinner} />;
  } else {
    Button = (
      <button
        aria-label="Submit"
        disabled={!emailAddress.length || isFormCompleted}
        onClick={handleSubmit}
        type="submit"
      >
        <RightArrowIcon />
      </button>
    );
  }

  return (
    <form className={`${styles.form} ${className}`} onSubmit={handleSubmit}>
      <FloatingLabel
        dark
        error={emailError ? ERROR_MESSAGES[emailError] : undefined}
        buttonComponent={Button}
      >
        <input
          aria-label="Email Address"
          id="webFooter-emailAddress"
          name="emailAddress"
          onChange={e => {
            setEmailError(undefined);
            setEmailAddress(e.target.value);
          }}
          required
          readOnly={isFormCompleted}
          type="email"
          value={emailAddress}
        />
      </FloatingLabel>
      {!isFormCompleted && !emailError && (
        <p className={styles.caption}>
          See our <FFLink url="/privacy">Privacy Policy</FFLink>
        </p>
      )}
      {isFormCompleted && (
        <p className={styles.caption} ref={successMessageRef} tabIndex={-1}>
          Thanks for submitting!
        </p>
      )}
    </form>
  );
}
